import { useState, useCallback } from 'react';
import { useMount } from 'ahooks';
import type {
    FilterState,
    FilterStates,
    FilterStatesKey,
} from '../type/filter-state';
import type { SearchIndex } from '../type/api';
import type { AggregateState } from './use-aggregation';
import { useAggregation } from './use-aggregation';

interface Options {
    /** The search type/index, e.g. product, project, professional. */
    category?: SearchIndex;
    /** The initial filter states. */
    initialStates: FilterStates;
    /** This hook is used in the product suppliers page. */
    isSuppliersPage?: boolean;
}

export const ES_AGGREGATE_API = '/remix-api/search/aggregate';

const cloneDeep = (state: FilterStates): FilterStates => {
    return JSON.parse(JSON.stringify(state));
};

/**
 * Hook to manage filter states for mobile directory pages.
 * @todo Replace with `useSearchFilters`, which does many of the same things.
 * @see {@link packages/web/app/modules/search/hook/use-search-filters.tsx}
 */
const useFilterStates = ({
    category,
    initialStates,
    isSuppliersPage = false,
}: Options) => {
    const [filterStates, setFilterStates] = useState<FilterStates>(
        // we will update the filter states but won't affect the original one
        cloneDeep(initialStates),
    );
    const { aggregate, loading } = useAggregation({
        category,
        isSuppliersPage,
    });

    const fetchStates = useCallback(
        (states: FilterStates) => {
            aggregate({
                aggregateType: 'filterAggregation',
                filterStates: states,
                onAggregateStateChange: (
                    _,
                    state: AggregateState,
                    data?: FilterStates | undefined,
                ) => {
                    if (state !== 'loaded') return;
                    if (data) {
                        setFilterStates(data);
                    }
                },
            });
        },
        [aggregate],
    );

    // page loader doesn't return all filter states(ignore category tree) to improve performance
    useMount(() => fetchStates(filterStates));

    const updateFilter = useCallback(
        <T extends FilterState>(
            filterKey: FilterStatesKey,
            state: Partial<T>,
            updateStates = true,
            subFilterKey?: string,
        ) => {
            setFilterStates((preStates) => {
                const currentState = preStates[filterKey];
                let newStates: FilterStates;
                if (filterKey !== 'facetOptions') {
                    newStates = {
                        ...preStates,
                        [filterKey]: currentState
                            ? { ...currentState, ...state }
                            : state,
                    };
                } else {
                    const facetStates = filterStates[filterKey];
                    if (!facetStates || !facetStates.length) return preStates;
                    const facetOptionState = facetStates.find(
                        (facetState) => facetState.id === subFilterKey,
                    );
                    if (!facetOptionState) return preStates;
                    facetOptionState.state = {
                        ...facetOptionState.state,
                        ...state,
                    };
                    newStates = {
                        ...preStates,
                        facetOptions: [...facetStates],
                    };
                }
                if (updateStates) fetchStates(newStates);
                return newStates;
            });
        },
        [fetchStates, filterStates],
    );

    const resetFilters = useCallback(
        (states: FilterStates) => {
            setFilterStates(states);
            fetchStates(states);
        },
        [fetchStates],
    );

    return {
        loading,
        filterStates,
        fetchStates,
        updateFilter,
        resetFilters,
        aggregate,
    };
};

export { useFilterStates };
